import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';

const OrdersTable = ({ orders, loading, onStatusUpdate }) => {
  const [statusNote, setStatusNote] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // If already a string, return as is
    if (typeof timestamp === 'string') return timestamp;
    
    // Convert Firestore Timestamp to JavaScript Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    const statusClasses = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Confirmed: 'bg-orange-100 text-orange-800',
      Processing: 'bg-purple-100 text-purple-800',
      Shipped: 'bg-indigo-100 text-indigo-800',
      Delivered: 'bg-green-100 text-green-800',
      Cancelled: 'bg-red-100 text-red-800',
    };
    

    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getOrderTypeBadge = (order) => {
  if (order.orderType === 'supplier_pickup') {
    return (
      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        📦 Pickup Order
      </span>
    );
  }
  return null;
};

  
  // Open status update modal
  const openStatusModal = (order, status) => {
    setSelectedOrder(order);
    setNewStatus(status);
    setStatusNote({...statusNote, [order.id]: statusNote[order.id] || ''});
    setShowStatusModal(true);
  };
  
  // Close status update modal
  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedOrder(null);
    setNewStatus('');
  };
  
  // Handle status update
  const handleStatusUpdate = () => {
    if (selectedOrder && newStatus) {
      onStatusUpdate(selectedOrder.id, newStatus, statusNote[selectedOrder.id] || '');
      closeStatusModal();
    }
  };
  
  // Get next possible statuses
  const getNextStatuses = (currentStatus) => {
    switch (currentStatus) {
      case 'Pending':
        return ['Confirmed', 'Shipped', 'Cancelled'];
      case 'Confirmed':
        return ['Processing', 'Shipped', 'Cancelled'];
      case 'Processing':
        return ['Shipped', 'Cancelled'];
      case 'Shipped':
        return ['Delivered', 'Cancelled'];
      case 'Delivered':
        return [];
      case 'Cancelled':
        return [];
      default:
        return ['Pending', 'Confirmed', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
    }
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Order ID
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading && orders.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
                <p className="mt-2">Loading orders...</p>
              </td>
            </tr>
          ) : orders.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                No orders found.
              </td>
            </tr>
          ) : (
            orders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
  <Link to={`/orders/${order.id}`}>
    {order.id}
  </Link>
  {getOrderTypeBadge(order)}
</td>
               <td className="px-6 py-4 whitespace-nowrap">
  <div className="text-sm font-medium text-gray-900">
    {order.orderType === 'supplier_pickup' 
      ? order.sourceVendorName || order.customerName 
      : order.customerName || 'Unknown Customer'}
  </div>
  <div className="text-sm text-gray-500">
    {order.orderType === 'supplier_pickup' 
      ? `From: ${order.customerEmail || 'No email'}` 
      : order.customerEmail || 'No email'}
  </div>
  {order.orderType === 'supplier_pickup' && (
    <div className="text-xs text-purple-600 mt-1">
      Deliver to: {order.deliveryCustomerName}
    </div>
  )}
</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(order.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(order.total || 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(order.status || 'Pending')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/orders/${order.id}`}
                      className="text-orange-600 hover:text-orange-900"
                    >
                      View
                    </Link>
                    <span className="text-gray-300">|</span>
                    
                    {/* Status Update Dropdown */}
                    <div className="relative">
                      <select
                        className="block w-32 pl-3 pr-10 py-1 text-xs border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 rounded-md"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            openStatusModal(order, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        disabled={getNextStatuses(order.status).length === 0}
                      >
                        <option value="">Update Status</option>
                        {getNextStatuses(order.status).map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 издатель sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Update Order Status
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Update the status of order <span className="font-medium">{selectedOrder.id}</span> from <span className="font-medium">{selectedOrder.status}</span> to <span className="font-medium">{newStatus}</span>.
                      </p>
                      
                      <div className="mt-4">
                        <label htmlFor="statusNote" className="block text-sm font-medium text-gray-700">
                          Add a note (optional)
                        </label>
                        <textarea
                          id="statusNote"
                          rows={3}
                          className="mt-1 block w-full shadow-sm focus:ring-orange-500 focus:border-orange-500 sm:text-sm border border-gray-300 rounded-md"
                          placeholder="Enter any additional information about this status change..."
                          value={statusNote[selectedOrder.id] || ''}
                          onChange={(e) => setStatusNote({...statusNote, [selectedOrder.id]: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleStatusUpdate}
                >
                  Update Status
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeStatusModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersTable;